// Right-click dropdown for assistant messages.
// Two items: immediate Copy (markdown) and Copy Special… (opens the full dialog).
// Portal-rendered, reuses the malpedia-context-menu CSS classes.

import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'sonner';
import type { SourceDoc } from '../types';
import { buildCopy, type CopyScope } from '../lib/copyFormat';
import { getActiveZoom } from '../lib/zoomPosition';

interface CopyContextMenuProps {
  scope: CopyScope;
  body: string;
  sources?: SourceDoc[];
  position: { x: number; y: number };
  onOpenSpecial: () => void;
  onClose: () => void;
}

export default function CopyContextMenu({
  scope,
  body,
  sources,
  position,
  onOpenSpecial,
  onClose,
}: CopyContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  const z = getActiveZoom();
  const style: React.CSSProperties = {
    top: Math.min(position.y / z, window.innerHeight / z - 100),
    left: Math.min(position.x / z, window.innerWidth / z - 200),
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleCopy = () => {
    const text = buildCopy(body, sources, {
      scope,
      refFormat: 'both',
      format: 'markdown',
      includeSourceTable: false,
    });
    void navigator.clipboard.writeText(text);
    toast.success('Copied');
    onClose();
  };

  return ReactDOM.createPortal(
    <div ref={menuRef} className="malpedia-context-menu" style={style} role="menu">
      <button
        className="malpedia-context-menu-item"
        role="menuitem"
        onClick={handleCopy}
      >
        Copy
      </button>
      <button
        className="malpedia-context-menu-item"
        role="menuitem"
        onClick={() => {
          onOpenSpecial();
          onClose();
        }}
      >
        Copy Special…
      </button>
    </div>,
    document.body,
  );
}
